import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SkewT } from '../../lib/skewt';
import { drawExtensions } from '../../lib/skewt-extensions';

const HELP_TERM_KEYS = [
    'cape',
    'cin',
    'lcl',
    'lfc',
    'el',
    'redLine',
    'greenLine',
    'blueLine',
    'pinkPath',
    'greyAreas',
];

/**
 * Mount the vendored skewt-js chart inside a div ref.
 *
 * - On mount: instantiate `new SkewT(divEl, { unit: 'ms' })` (m/s for European pilots).
 * - On data change: call `chart.plot(data)` to redraw the curves.
 * - On unmount: `chart.destroy()` releases the ResizeObserver and SVG nodes.
 *
 * NOTE: the inner div uses `data-testid="skewt-chart-inner"` to avoid colliding
 * with the parent SkewTPanel's `data-testid="skewt-chart"` (which `getByTestId`
 * in the panel test would otherwise see as a duplicate).
 */
export default function SkewTChart({ data }) {
    const { t, i18n } = useTranslation();
    const divRef = useRef(null);
    const chartRef = useRef(null);

    const helpI18n = useMemo(() => ({
        title: t('skewt.help.title'),
        terms: HELP_TERM_KEYS.map((key) => ({
            term: t(`skewt.help.${key}.term`),
            def: t(`skewt.help.${key}.def`),
        })),
    }), [t, i18n.language]);

    const pillboxI18n = useMemo(() => ({
        topOfThermals: t('Top of Thermals'),
        condensationLayer: t('Condensation Layer'),
        cuCloudbase: t('Cu Cloudbase'),
        equilibriumLevel: t('Equilibrium Level'),
    }), [t, i18n.language]);

    const cloudAxisI18n = useMemo(() => ({
        cloudCover: t('Cloud Cover'),
    }), [t, i18n.language]);

    const windAxisI18n = useMemo(() => ({
        wind: t('Wind (m/s)'),
    }), [t, i18n.language]);

    useEffect(() => {
        if (!divRef.current || chartRef.current) return;
        chartRef.current = new SkewT(divRef.current, { unit: 'ms' });
        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (chartRef.current && Array.isArray(data) && data.length > 0) {
            chartRef.current.plot(data);
            drawExtensions(chartRef.current, data, { helpI18n, pillboxI18n, cloudAxisI18n, windAxisI18n });
        }
    }, [data, helpI18n, pillboxI18n, cloudAxisI18n, windAxisI18n]);

    return <div ref={divRef} className="h-full w-full" data-testid="skewt-chart-inner" />;
}
